import { cn } from "lib/utils";
import { cloneElement, forwardRef, type ReactElement } from "react";

/** クラスを足せる要素（`className` を受け取る DOM 要素であること） */
type ClassNameElement = ReactElement<{ className?: string }>;

export type StickyAxisScrollFrameProps = {
  /** 左端に貼り付ける y 軸（`sticky` させる側） */
  axis: ClassNameElement;
  /** 横に流すプロット領域 */
  plot: ClassNameElement;
  /**
   * `axis` の実寸の幅（px）。`scroll-padding-left` に使う。
   *
   * 省略可能にしない。軸が左端を覆うぶんの補正が抜けると、キーボードで
   * リンクへ移動したときにフォーカスが軸の裏に隠れる（WCAG 2.4.11）。
   * 渡し忘れを型で落とすために必須にしている。
   */
  axisWidthPx: number;
};

/**
 * 枠が要求するクラスを要素に足す。
 *
 * ラッパーの div で包まないのは、包むとインライン要素になった svg の下に
 * ベースラインぶんの隙間が出て、いまの見た目が変わってしまうため。
 * @param {ClassNameElement} element 対象の要素
 * @param {string} className 足すクラス
 * @return {ClassNameElement} クラスを足した要素
 */
const withClass = (
  element: ClassNameElement,
  className: string,
): ClassNameElement =>
  cloneElement(element, {
    className: cn(element.props.className, className),
  });

/**
 * 「y 軸だけ左端に貼り付けて、プロット領域だけ横スクロールさせる」枠。
 *
 * この組み方は 3 要素・5 クラスが**揃って初めて**成立し、どれが欠けても
 * jsdom はレイアウトを計算しないので気付けない（実際にこの箇所は 2 回壊れた）。
 * そこでクラスの持ち主をこのコンポーネント 1 つに閉じ、不変条件の所有者を
 * 1 か所にする。ここでの `className` はこのコンポーネントの仕様そのものなので、
 * 同居する spec がクラス名を直接検査する。
 *
 * - `overflow-x-auto`（枠）: 唯一のスクロールボックス。左右を別々のコンテナに
 *   分けると、横スクロールバーが内容の下に重ならない環境（Windows / Linux）で
 *   左右の縦位置がバーの高さぶんずれる
 * - `flex w-max`（行）: `position: sticky` が貼り付く範囲はスクロールコンテナ
 *   ではなく「自分の包含ブロック」＝この行。`w-max` が無いと行が枠幅で止まり、
 *   末尾までスクロールしたところで y 軸が剥がれて流れていく
 * - `sticky left-0`（軸）: 貼り付けそのもの
 * - `z-10`（軸）: プロットは DOM 上あとに来るので、無いと棒が y 軸の上に乗る
 * - `bg-base-100`（軸）: 背景が透けると裏を流れる棒が見える
 * - `shrink-0`（軸・プロット）: flex 項目として縮められると、目盛が潰れ、
 *   プロットは横スクロールにならずに棒が潰れる
 *
 * `scroll-padding-left`（軸幅ぶん）もここが持つ。ブラウザがフォーカスを画面内へ
 * スクロールするとき `position: sticky` の要素は考慮されないので、これが無いと
 * 左端のリンクへ Tab で移動したときにフォーカスが y 軸の裏に潜る（WCAG 2.4.11
 * Focus Not Obscured (Minimum) / AA）。軸を貼り付けているのはこのコンポーネント
 * なので、その副作用の打ち消しもここの責務にする。値だけは軸の実寸を知っている
 * 呼び出し側から受け取る（`axis` の `props.width` を読むと型アサーションが要り、
 * `width` を持たない要素を渡されたときに静かに 0 になるため）。
 *
 * `ref` はスクロール枠（`overflow-x-auto` の div）に付く。呼び出し側は
 * `scrollLeft` / `clientWidth` の実測にこれを使う。
 * @param {StickyAxisScrollFrameProps} props y 軸とプロット領域
 * @return {JSX.Element} スクロール枠
 */
export const StickyAxisScrollFrame = forwardRef<
  HTMLDivElement,
  StickyAxisScrollFrameProps
>(({ axis, plot, axisWidthPx }, ref) => (
  <div
    ref={ref}
    className="overflow-x-auto"
    style={{ scrollPaddingLeft: axisWidthPx }}
  >
    <div className="flex w-max items-start">
      {withClass(axis, "sticky left-0 z-10 shrink-0 bg-base-100")}
      {withClass(plot, "shrink-0")}
    </div>
  </div>
));
StickyAxisScrollFrame.displayName = "StickyAxisScrollFrame";
